import { breakableSpaces } from "../../model/html-text";
import { Component, Input, OnChanges } from "@angular/core";
import { TimeLine } from "../../model/time-line";
import { Labeler } from "../../model/labeler";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { StatusService } from "../../services/status.service";
import { PrioritiesService } from "../../services/priorities.service";
import { BranchService } from "../../services/branch.service";

import { DepartmentsService } from "../../services/departaments.service";
import { UsersService } from "../../services/users.service";
import { InvolvedService } from "../../services/involved.service";
import { Involved } from "../../model/involved";
import { AuthService } from "../../auth.service";
import { TranslateService } from "@ngx-translate/core";
import { CommentsService } from "../../services/comments.service";
import { MediaService } from "../../services/media.service";
import { SafeUrl } from "@angular/platform-browser";
import { environment } from "../../../environments/environment";
import { Media } from "../../model/media";
import { Messages } from "../../model/messages";

/** The fields whose changes are drawn as pills, and grouped when saved together. */
const FIELD_TOPICS = ["status", "priority", "department", "branch", "assigned"];

/** Events by the same person this close together were one save, and are one entry. */
const SAME_SAVE_MS = 60 * 1000;

/** One changed field within an entry: from one value to another, or set for the first time. */
export interface TimelineChange {
  topic: string;
  /** Translation key of the field's name. */
  field: string;
  oldReference: number | null;
  reference: number;
  labeler: Labeler;
  /** The list's pill class for this field, so it is drawn as it is there. */
  pillClass: string;
}

/** One entry in the timeline: what happened, who did it and when, with what it changed. */
export interface TimelineEntry {
  kind: "fields" | "comment" | "media" | "involved" | "recurrence" | "ticket" | "created" | "other";
  icon: string;
  /** Translation key and parameters of the line that says what happened. */
  title: string;
  titleParams?: { [key: string]: any };
  user: string;
  time: Date;
  /** For entries that stand for several events: the earliest of them. */
  since?: Date;
  changes: TimelineChange[];
  /** For a comment: its text, as html. */
  comment?: string;
  commentLong?: boolean;
  expanded?: boolean;
  /** For attachments: every file, so what is shown matches the count. */
  attachments: TimelineAttachment[];
  /** A sentence of its own, for what has nothing to show as values. */
  text?: string;
}

/** One attached file: a thumbnail for an image, otherwise an icon for its kind and its name. */
export interface TimelineAttachment {
  name: string;
  preview: SafeUrl | null;
  icon: string;
  /** Removed since it was added: counted, and shown as one tile with the others that went. */
  gone: boolean;
}

/** What browsers show as a picture, also when the file was stored without its type. */
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

export interface TimelineDay {
  label: string;
  entries: TimelineEntry[];
}

/**
 * What happened to a ticket, newest first, one day under another (#173).
 *
 * The API stores an event for every change. Shown one by one, a single save
 * became four entries with the same person and minute, so changes by one
 * person within a minute are one entry, a line per field. Values are the
 * pills the list draws them with. The ticket's own creation closes the
 * timeline at the bottom, from the ticket itself rather than from an event,
 * so it is there also for tickets from before the events were kept.
 */
@Component({
  selector: "ticket-timeline",
  templateUrl: "./ticket-timeline.component.html",
  styleUrls: ["./ticket-timeline.component.css"],
})
export class TicketTimelineComponent implements OnChanges {
  @Input() events: TimeLine[] = [];
  /** When and by whom the ticket was made: the entry the timeline ends with. */
  @Input() createdAt: string | null = null;
  @Input() createdBy: number | null = null;
  @Input() referenceLabeler: Labeler | null = null;
  @Input() items: TimeLine[] = [];
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);
  days: TimelineDay[] = [];
  private involvedCache: { [id: number]: Involved } = {};

  constructor(
    private priority: PrioritiesService,
    private status: StatusService,
    private branch: BranchService,
    private user: UsersService,
    private department: DepartmentsService,
    private involvedService: InvolvedService,
    private authService: AuthService,
    private commentsService: CommentsService,
    private mediaService: MediaService,
    private translate: TranslateService
  ) {}

  /** Also on the first binding, which comes before init. */
  ngOnChanges(): void {
    this.loadEvents();
  }

  async loadEvents(): Promise<void> {
    const token = this.authService.token;
    const activeInvolvedIds = this.involvedService.InvolvedData.map((i) => i.id);

    const isCreation = (event: TimeLine) =>
      event.topic === "ticket" && (event.action ?? "").includes("ticket.create");
    const creation = (this.events || []).find(isCreation) ?? null;

    const events = (this.events || []).filter((event) => {
      // the ticket's creation is the fixed last entry, not one among the others
      if (isCreation(event)) {
        return false;
      }
      if (event.topic === "ticket_involved") {
        return (
          activeInvolvedIds.includes(Number(event.reference)) ||
          activeInvolvedIds.includes(Number(event.oldReference))
        );
      }
      return true;
    });

    const single = await Promise.all(events.map((event) => this.toEntry(token, event)));
    const entries = this.groupSaves(
      single.filter((e): e is TimelineEntry => e !== null).sort((a, b) => b.time.getTime() - a.time.getTime())
    );

    // the creation in its own place in time: imported events can be older
    // than the ticket, and they stay visible below it rather than being hidden
    const created = await this.createdEntry(creation);
    if (created) {
      const at = entries.findIndex((e) => e.time.getTime() < created.time.getTime());
      entries.splice(at === -1 ? entries.length : at, 0, created);
    }
    this.days = this.byDay(entries);
  }

  /** The entry for one event from the API, before saves are grouped. */
  private async toEntry(token: string, event: TimeLine): Promise<TimelineEntry | null> {
    const time = this.parseTime(event.changed);
    if (!time) {
      return null;
    }
    const base = {
      user: await this.userName(event.actor),
      time,
      changes: [] as TimelineChange[],
      attachments: [] as TimelineAttachment[],
    };
    const topic = event.topic ?? "";
    const action = event.action ?? "";

    if (FIELD_TOPICS.includes(topic)) {
      const labeler = this.getLabeler(topic)!;
      const old = Number(event.oldReference);
      // saved without a change: nothing happened to show
      if (old && old === Number(event.reference)) {
        return null;
      }
      const change: TimelineChange = {
        topic,
        field: `timeline.field.${topic}`,
        oldReference: old ? old : null,
        reference: Number(event.reference),
        labeler,
        pillClass: PILL_CLASS[topic],
      };
      return {
        ...base,
        kind: "fields",
        icon: "edit",
        title: change.oldReference === null ? "timeline.set" : "timeline.changed",
        titleParams: { field: this.translate.instant(change.field) },
        changes: [change],
      };
    }

    if (topic === "comment") {
      const content = typeof event.reference === "number" ? await this.commentContent(token, event.reference) : "";
      return {
        ...base,
        kind: "comment",
        icon: "chat_bubble_outline",
        title: action.includes("part.update") ? "timeline.comment.edited" : "timeline.comment.added",
        comment: breakableSpaces(content),
        // about four lines at this width; longer ones open with "meer"
        commentLong: this.plainText(content).length > 280,
      };
    }

    if (topic === "media") {
      const attachment =
        typeof event.reference === "number" ? await this.attachment(token, event.reference, event.ticket) : null;
      // a file of another ticket - old imported data has those - is not this ticket's history
      if (!attachment) {
        return null;
      }
      return {
        ...base,
        kind: "media",
        icon: "attach_file",
        title: "timeline.media.added",
        titleParams: { count: 1 },
        attachments: [attachment],
      };
    }

    if (topic === "ticket_involved") {
      const name = await this.involvedName(
        token,
        Number(action.includes("part.create") ? event.reference : event.reference || event.oldReference)
      );
      return {
        ...base,
        kind: "involved",
        icon: "group",
        title: action.includes("part.create") ? "timeline.involved.added" : "timeline.involved.removed",
        text: name,
      };
    }

    if (topic === "recurrence" || topic === "terugkeerpatroon") {
      let key = "timeline.recurrence.generated";
      if (topic === "recurrence") {
        if (action.includes("part.create")) key = "timeline.recurrence.attached";
        else if (action.includes("part.update")) key = "timeline.recurrence.changed";
        else key = "timeline.recurrence.removed";
      }
      return { ...base, kind: "recurrence", icon: "repeat", title: key };
    }

    if (topic === "ticket") {
      if (action.includes("ticket.archive")) {
        return { ...base, kind: "ticket", icon: "inventory_2", title: "timeline.ticket.archived" };
      }
      return { ...base, kind: "ticket", icon: "edit_note", title: "timeline.ticket.updated" };
    }

    return { ...base, kind: "other", icon: "circle", title: "timeline.other" };
  }

  /**
   * Field changes, and attachments, by the same person within a minute of each
   * other were one save: one entry, a line per field or a preview per file.
   */
  private groupSaves(entries: TimelineEntry[]): TimelineEntry[] {
    const grouped: TimelineEntry[] = [];
    for (const entry of entries) {
      const last = grouped[grouped.length - 1];
      const sameSave =
        last &&
        last.kind === entry.kind &&
        (entry.kind === "fields" || entry.kind === "media") &&
        last.user === entry.user &&
        Math.abs(last.time.getTime() - entry.time.getTime()) <= SAME_SAVE_MS &&
        // the same field twice is two saves, not one
        !(entry.kind === "fields" && last.changes.some((c) => c.topic === entry.changes[0].topic));

      if (!sameSave) {
        grouped.push(entry);
        continue;
      }
      if (entry.kind === "fields") {
        last.changes.push(...entry.changes);
        last.title = "timeline.changedMany";
        last.titleParams = { count: last.changes.length };
      } else {
        last.attachments.push(...entry.attachments);
        last.title = "timeline.media.addedMany";
        last.titleParams = { count: last.attachments.length };
      }
    }
    // the fields in a save in the order the details show them
    for (const entry of grouped) {
      entry.changes.sort((a, b) => FIELD_TOPICS.indexOf(a.topic) - FIELD_TOPICS.indexOf(b.topic));
    }
    return this.foldUpdates(grouped);
  }

  /**
   * The API records "ticket updated" on every save, also on one whose fields
   * have their own entry: there it says nothing more, and goes. What is left -
   * saves of the title or the description - comes in runs; a run by the same
   * person on the same day is one entry that says how often.
   */
  private foldUpdates(entries: TimelineEntry[]): TimelineEntry[] {
    const isUpdate = (e: TimelineEntry) => e.kind === "ticket" && e.title.startsWith("timeline.ticket.updated");
    const partOfSave = (update: TimelineEntry) =>
      entries.some(
        (e) =>
          e.kind === "fields" &&
          e.user === update.user &&
          Math.abs(e.time.getTime() - update.time.getTime()) <= SAME_SAVE_MS
      );

    const folded: TimelineEntry[] = [];
    for (const entry of entries) {
      if (isUpdate(entry) && partOfSave(entry)) {
        continue;
      }
      const last = folded[folded.length - 1];
      if (
        last &&
        isUpdate(entry) &&
        isUpdate(last) &&
        last.user === entry.user &&
        this.dayLabel(last.time) === this.dayLabel(entry.time)
      ) {
        const count = (last.titleParams?.["count"] ?? 1) + 1;
        last.title = "timeline.ticket.updatedMany";
        last.titleParams = { count };
        last.since = entry.time;
        continue;
      }
      folded.push(entry);
    }
    return folded;
  }

  /**
   * The fixed last entry: the ticket made, when and by whom. From its creation
   * event where the API kept one - the ticket's own date need not be the moment
   * it was saved - and from the ticket itself for those from before the events.
   */
  private async createdEntry(creation: TimeLine | null): Promise<TimelineEntry | null> {
    const time = this.parseTime(creation?.changed) ?? this.parseTime(this.createdAt);
    if (!time) {
      return null;
    }
    const by = creation?.actor || this.createdBy;
    return {
      kind: "created",
      icon: "add_circle_outline",
      title: "timeline.created",
      user: by ? await this.userName(by) : "",
      time,
      changes: [],
      attachments: [],
    };
  }

  /** Entries under a heading per day: today, yesterday, or the date. */
  private byDay(entries: TimelineEntry[]): TimelineDay[] {
    const days: TimelineDay[] = [];
    for (const entry of entries) {
      const label = this.dayLabel(entry.time);
      const last = days[days.length - 1];
      if (last && last.label === label) {
        last.entries.push(entry);
      } else {
        days.push({ label, entries: [entry] });
      }
    }
    return days;
  }

  private dayLabel(time: Date): string {
    const day = new Date(time.getFullYear(), time.getMonth(), time.getDate()).getTime();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (day === today) {
      return this.translate.instant("timeline.today");
    }
    if (day === today - 24 * 60 * 60 * 1000) {
      return this.translate.instant("timeline.yesterday");
    }
    const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "long" };
    if (time.getFullYear() !== now.getFullYear()) {
      options.year = "numeric";
    }
    return new Intl.DateTimeFormat(this.translate.currentLang, options).format(time);
  }

  timeOfDay(time: Date): string {
    return new Intl.DateTimeFormat(this.translate.currentLang, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(time);
  }

  /**
   * The API adds the zone's name in brackets - "2024-12-10T05:08:14-03:00[America/Fortaleza]" -
   * which Date cannot read; the offset before it says all it needs.
   */
  private parseTime(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value.replace(/\[.*\]$/, ""));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private plainText(html: string): string {
    return (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  toggle(entry: TimelineEntry): void {
    entry.expanded = !entry.expanded;
  }

  /** The person behind an event; nobody for what the system did by itself. */
  private async userName(actorId: number): Promise<string> {
    if (!actorId) {
      return this.translate.instant("timeline.system");
    }
    try {
      return await firstValueFrom(this.user.makeLabel(actorId));
    } catch {
      return `[${actorId}]`;
    }
  }

  getLabeler(topic: string): Labeler | null {
    const labelers: { [key: string]: Labeler } = {
      status: this.status,
      priority: this.priority,
      department: this.department,
      branch: this.branch,
      assigned: this.user,
    };
    return labelers[topic] || null;
  }

  private async involvedName(token: string, involvedId: number): Promise<string> {
    if (!involvedId) {
      return "";
    }
    const involved = await this.getInvolvedById(token, involvedId);
    const userId = involved ? Number(involved.involved) : involvedId;
    return this.userName(userId);
  }

  private async getInvolvedById(token: string, involvedId: number): Promise<Involved | null> {
    if (this.involvedCache[involvedId]) {
      return this.involvedCache[involvedId];
    }
    return new Promise((resolve) => {
      this.involvedService.getInvolvedById(token, involvedId).subscribe(() => {
        const found = this.involvedService.InvolvedData.find((i) => i.id === involvedId) ?? null;
        if (found) {
          this.involvedCache[involvedId] = found;
        }
        resolve(found);
      });
    });
  }

  private async commentContent(token: string, commentId: number): Promise<string> {
    return new Promise((resolve) => {
      this.commentsService.getCommentById(token, commentId).subscribe({
        next: () => {
          const comment = this.commentsService.commentsData.find((c) => c.id === commentId);
          resolve(comment?.content ?? "");
        },
        error: () => resolve(""),
      });
    });
  }

  /** A file as the timeline shows it: an image as a thumbnail, anything else as an icon and its name. */
  private async attachment(token: string, mediaId: number, ticketId: number): Promise<TimelineAttachment | null> {
    let media: Media | null = null;
    try {
      media = await firstValueFrom(this.mediaService.getMediaByIdRaw(token, mediaId));
    } catch {
      media = null;
    }
    // removed since: the event still counts it, so it is shown as gone
    if (!media) {
      return { name: "", preview: null, icon: "hide_source", gone: true };
    }
    if (ticketId && media.ticketId && media.ticketId !== ticketId) {
      return null;
    }
    const name = media.fileInfo?.fileName ?? "";
    const type = media.contentType ?? "";
    const extension = name.split(".").pop()?.toLowerCase() ?? "";
    let preview: SafeUrl | null = null;
    if (name && (type.startsWith("image/") || IMAGE_EXTENSIONS.includes(extension))) {
      const url = `${environment.API_BASE_URL}media/for-ticket/${media.ticketId}/download/${media.id}/${name}`;
      preview = await this.mediaService.downloadAuthenticatedMediaUrlAsSafeUrl(url, token);
    }
    return { name, preview, icon: this.fileIcon(type, name), gone: false };
  }

  /** The files still there, each a tile of its own. */
  present(entry: TimelineEntry): TimelineAttachment[] {
    return entry.attachments.filter((a) => !a.gone);
  }

  /** How many went since: one tile says so for all of them. */
  goneCount(entry: TimelineEntry): number {
    return entry.attachments.filter((a) => a.gone).length;
  }

  /** The icon for a file that is not shown as a thumbnail, by its kind. */
  private fileIcon(type: string, name: string): string {
    const extension = name.split(".").pop()?.toLowerCase() ?? "";
    if (type === "application/pdf" || extension === "pdf") return "picture_as_pdf";
    if (type.startsWith("video/")) return "movie";
    if (type.startsWith("audio/")) return "audiotrack";
    if (type.startsWith("image/")) return "image";
    if (type.startsWith("text/") || ["doc", "docx", "odt", "rtf", "txt"].includes(extension)) return "description";
    if (["xls", "xlsx", "ods", "csv"].includes(extension)) return "table_chart";
    return "insert_drive_file";
  }

  /** Drops what is shown of an involved person the user has just removed. */
  removeInvolvedEvents(deletedInvolvedId: number): void {
    this.events = this.events.filter(
      (event) =>
        !(
          event.topic === "ticket_involved" &&
          (Number(event.reference) === deletedInvolvedId || Number(event.oldReference) === deletedInvolvedId)
        )
    );
    this.loadEvents();
  }
}

/** The class the ticket list draws each field's pill with. */
const PILL_CLASS: { [topic: string]: string } = {
  status: "sts",
  priority: "prio",
  department: "dep",
  branch: "dc",
  assigned: "usr",
};
